import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Add as AddIcon, Person as PersonIcon } from "../../_core/components/icons";
import { makeStyles, UICircularProgress } from "../../_core/components";
import Main from "../../_core/components/layout/Main";
import UITable from "../../_core/components/ui/UITable";
import UIButton from "../../_core/components/ui/UIButton";
import { useApiAlerts, useTableControls } from "../../_core/hooks";
import UIBack from "../../_core/components/ui/UIBack";
import { formatDate } from "../../_core/utils";
import { useUsers } from "../hooks";
import { AccountLevel, accountLevelToString, UserLite } from "../api/types";
import { impersonateHubUser } from "../../auth/api/utils";
import { useAuthSession } from "../../auth/hooks";
import UserStatusChip from "../components/UserStatusChip";
import ActionsDropdown from "../components/ActionsDropdown";
import UIEmailWithVerification from "../../_core/components/ui/UIEmailWithVerification";
import MfaIcon from "../components/MfaIcon";

const Users = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const controls = useTableControls();
  const { data: session } = useAuthSession();
  const [impersonatingUuid, setImpersonatingUuid] = useState<string | null>(null);
  const [impersonatingError, setImpersonatingError] = useState<any>();
  const queryParams = new URLSearchParams(location.search);
  const accountId =
    queryParams.get("accountId") ??
    (() => {
      throw new Error("accountId is required here");
    })();
  const accountCode = queryParams.get("accountCode");
  const accountLevel = queryParams.get("level");

  if (!accountId || !accountCode) {
    navigate(accountCode ? `/?search=${accountCode}` : "/");
  }

  const { data, isFetching, isError, error } = useUsers(
    {
      page: controls.page,
      size: controls.rowsPerPage,
      accountId: accountId || ""
    },
    { enabled: Boolean(accountId) }
  );

  useApiAlerts(undefined, error || impersonatingError, isError || !!impersonatingError);

  const handleImpersonate = async (targetUser: UserLite) => {
    setImpersonatingUuid(targetUser.id);
    try {
      await impersonateHubUser(targetUser, session, accountId);
    } catch (e) {
      setImpersonatingError(e);
    } finally {
      setImpersonatingUuid(null);
      setTimeout(() => setImpersonatingError(null));
    }
  };

  return (
    <Main
      title={`Users (${accountCode})`}
      data-testid="users-container"
      action={{
        label: "Add User",
        disabled: accountLevel === AccountLevel.SubAccount,
        tooltipText:
          accountLevel === AccountLevel.SubAccount ? "You are unable to create a sub-account user in this portal." : "",
        startIcon: <AddIcon />,
        onClick: () => navigate("/customer-users/new", { state: { accountId, accountCode } })
      }}
    >
      <div className={classes.root}>
        <UIBack label="Back To Accounts" />
        <UITable
          className={classes.table}
          isLoading={isFetching}
          columns={["Name", "Email", "User Access", "Last Activity", "Status", "MFA", "Masquerade", "Actions"]}
          data={data?.content}
          rowCallback={(u: UserLite) => [
            u.name,
            <UIEmailWithVerification
              key={`email-${u.id}`}
              email={u.email}
              isValidated={u.validated}
              emailDeliveryFailure={u.emailDeliveryFailure}
            />,
            u.userAccess.map((accountLevel: AccountLevel) => accountLevelToString[accountLevel] || "Unknown").join(", "),
            formatDate(u.lastActivity),
            <UserStatusChip key={`status-${u.id}`} isActive={u.status === "ACTIVE"} />,
            <MfaIcon key={`mfa-${u.id}`} isEnabled={u.mfaEnabled} />,
            <UIButton
              key={`masquerade-${u.id}`}
              data-testid="impersonate-btn"
              isLoading={impersonatingUuid === u.id}
              disabled={impersonatingUuid === u.id}
              onClick={() => handleImpersonate(u)}
              startIcon={impersonatingUuid === u.id ? <UICircularProgress size={18} /> : <PersonIcon />}
            >
              Masquerade
            </UIButton>,
            <ActionsDropdown key={`actions-${u.id}`} user={u} accountId={accountId} />
          ]}
          options={{
            ...controls,
            count: data?.totalElements
          }}
        />
      </div>
    </Main>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 8
  },
  table: {
    paddingTop: 22
  },
  resend: {
    minWidth: 180
  }
}));

export default Users;
